/**
 * Helpers for the member contact list and custom fields (membermanagement 003): display formats, the
 * client-side checks that mirror the API rules and value conversion for the custom field inputs.
 * The API stays the authority: it normalises phones to E.164 and every custom value to its canonical text.
 */
import dayjs from 'dayjs';
import { MemberContactIds, type MemberContactLookupItem, type MemberCustomFieldEntryView } from '@actschurch/ccms-sdk';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** True when the text looks like a phone number (the API makes the final call). */
export function looksLikePhone(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 15;
}

export function looksLikeEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

/** "+27821234567" → "082 123 4567"; other numbers stay as they are. */
export function displayPhone(value: string): string {
  const v = value.trim();
  if (/^\+27\d{9}$/.test(v)) {
    const national = `0${v.slice(3)}`;
    return `${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6)}`;
  }
  return v;
}

/** The uses and labels that fit a kind (from the lookups). */
export function usesForKind(uses: MemberContactLookupItem[], kindId: number): MemberContactLookupItem[] {
  return uses.filter((u) => u.isActive && u.kindID === kindId).sort((a, b) => a.sortOrder - b.sortOrder);
}

export function labelsForKind(labels: MemberContactLookupItem[], kindId: number): MemberContactLookupItem[] {
  return labels.filter((l) => l.isActive && (l.kindID == null || l.kindID === kindId)).sort((a, b) => a.sortOrder - b.sortOrder);
}

export function defaultUsesForKind(kindId: number): number[] {
  return kindId === MemberContactIds.KindEmail ? [MemberContactIds.UseEmail] : [MemberContactIds.UseCalling];
}

export function defaultLabelForKind(kindId: number): number {
  return kindId === MemberContactIds.KindEmail ? MemberContactIds.LabelHome : MemberContactIds.LabelMobile;
}

/** Colours per use (badges). */
export const USE_COLORS: Record<number, string> = {
  [MemberContactIds.UseCalling]: 'blue',
  [MemberContactIds.UseWhatsApp]: 'green',
  [MemberContactIds.UseSms]: 'yellow',
  [MemberContactIds.UseEmail]: 'grape',
};

// ---------------------------------------------------------------- custom fields

export type CustomFieldTypeCode =
  | 'text' | 'textarea' | 'number' | 'decimal' | 'boolean' | 'date' | 'datetime'
  | 'select' | 'multiselect' | 'email' | 'phone' | 'url';

/** True when the field takes several values (multi-select always does). */
export function allowsMultiple(typeCode: string | null | undefined, allowMultiple: boolean): boolean {
  return typeCode === 'multiselect' || allowMultiple;
}

/** One stored value as people read it. */
export function formatCustomValue(typeCode: string | null | undefined, value: string): string {
  switch (typeCode) {
    case 'boolean':
      return value === 'true' ? 'Yes' : value === 'false' ? 'No' : value;
    case 'date': {
      const d = dayjs(value);
      return d.isValid() ? d.format('D MMMM YYYY') : value;
    }
    case 'datetime': {
      const d = dayjs(value);
      return d.isValid() ? d.format('D MMMM YYYY, HH:mm') : value;
    }
    case 'phone':
      return displayPhone(value);
    case 'decimal':
    case 'number': {
      const n = Number(value);
      return Number.isFinite(n) ? n.toLocaleString('en-ZA', { maximumFractionDigits: 6 }) : value;
    }
    default:
      return value;
  }
}

/** The values of a field as people read them. */
export function formatCustomEntry(entry: MemberCustomFieldEntryView): string[] {
  return entry.values.map((v) => formatCustomValue(entry.fieldTypeCode, v.value));
}

/** Date input value (yyyy-MM-dd text) → Date for the picker. */
export function dateFromValue(value: string): Date | null {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d.toDate() : null;
}

/** Date from the picker → the canonical text the API stores for a date field. */
export function dateToValue(date: Date | null): string {
  return date ? dayjs(date).format('YYYY-MM-DD') : '';
}

/** Date and time from the picker → ISO text (the API stores it in UTC). */
export function dateTimeToValue(date: Date | null): string {
  return date ? dayjs(date).toISOString() : '';
}

/** Choices typed one per line → a clean list (blanks and repeats dropped). */
export function parseChoices(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split('\n')) {
    const v = raw.trim();
    if (!v || seen.has(v.toLowerCase())) continue;
    seen.add(v.toLowerCase());
    out.push(v);
  }
  return out;
}

/** "Alternate WhatsApp number" → "alternate_whatsapp_number" (what the API makes when the key is left out). */
export function suggestFieldKey(label: string): string {
  const key = label
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!key) return 'field';
  return /^\d/.test(key) ? `field_${key}` : key.slice(0, 100);
}

export const FIELD_KEY_RE = /^[a-z][a-z0-9_]*$/;
